import { useFetchGetMe } from "hooks/usersHooks";
import {
  DIRECTION_ROLES,
  FACULTY_ROLES,
  GLOBAL_ROLES,
  SUPERUSER_ROLES,
  getRoleNames,
} from "./roles";

export { DIRECTION_ROLES, FACULTY_ROLES, GLOBAL_ROLES, SUPERUSER_ROLES };

export type MeAccess = {
  is_superuser?: boolean;
  is_global?: boolean;
  restricted?: boolean;
  faculty_ids?: number[];
  speciality_ids?: number[] | null;
};

export type MeScope = {
  role_id?: number;
  role_name?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  speciality_id?: number | null;
  speciality_name?: string | null;
};

export type Me = {
  id?: number;
  username?: string;
  roles?: { id?: number; name?: string }[];
  permissions?: string[];
  scopes?: MeScope[];
  access?: MeAccess | null;
  user?: any;
};

export function isSuperuser(me: Me | null | undefined): boolean {
  if (me?.access) return !!me.access.is_superuser;
  return getRoleNames(me?.roles).some((r) => SUPERUSER_ROLES.includes(r));
}

/**
 * Permission names come from the backend: `{view|create|edit|delete}_{resource}`,
 * e.g. `view_students`, `edit_user-access`. The API stays the source of truth.
 */
export function can(me: Me | null | undefined, permission: string): boolean {
  if (!me) return false;
  if (isSuperuser(me)) return true;
  return !!me.permissions?.includes(permission);
}

export function canAny(me: Me | null | undefined, permissions: string[]): boolean {
  return permissions.some((p) => can(me, p));
}

/** Sees the whole university (rector, super admin) — no faculty filter. */
export function isGlobal(me: Me | null | undefined): boolean {
  if (me?.access) return !me.access.restricted;
  return getRoleNames(me?.roles).some((r) => GLOBAL_ROLES.includes(r));
}

export function myFacultyIds(me: Me | null | undefined): number[] {
  if (me?.access?.faculty_ids) return me.access.faculty_ids;
  const ids = (me?.scopes ?? [])
    .map((s) => s.department_id)
    .filter((d): d is number => d != null)
    .map(Number);
  return Array.from(new Set(ids));
}

export function myDirectionIds(me: Me | null | undefined): number[] {
  if (me?.access?.speciality_ids) return me.access.speciality_ids;
  const ids = (me?.scopes ?? [])
    .map((s) => s.speciality_id)
    .filter((d): d is number => d != null)
    .map(Number);
  return Array.from(new Set(ids));
}

/** Filters a faculty option list down to the user's scope (no-op for global users). */
export function filterFacultyOptions<T>(
  me: Me | null | undefined,
  items: T[] | undefined | null,
  getId: (item: T) => number | string | null | undefined,
): T[] {
  if (!items) return [];
  if (isGlobal(me)) return items;
  const allowed = new Set(myFacultyIds(me).map(Number));
  return items.filter((i) => allowed.has(Number(getId(i))));
}

export function useAccess() {
  const { data: me, isLoading } = useFetchGetMe({ enabled: true });
  return {
    me: me as Me | undefined,
    isLoading,
    can: (permission: string) => can(me, permission),
    canAny: (permissions: string[]) => canAny(me, permissions),
    isGlobal: isGlobal(me),
    isSuperuser: isSuperuser(me),
    facultyIds: myFacultyIds(me),
    directionIds: myDirectionIds(me),
  };
}
